import React from 'react'
import { slugs } from '@/constants/slugs'
import { TranslateFunction, Translate } from '@/utils/translate/translate-client'

const PROJECT_STYLES: Record<string, string> = {
  [slugs.homestead]: '!font-[bio-sans] !text-homestead-blue',
  [slugs.kingOfKings]: '!font-[bio-sans] !text-king-of-kings-blue',
  [slugs.theLastRodeo]: '!font-[bio-sans] !text-last-rodeo-blue',
  [slugs.sketch]: ' !text-giveaway-subtle',
  [slugs.senior]: ' !text-giveaway-subtle',
  [slugs.david]: ' !text-giveaway-subtle',
}

export const getProjectStyle = (projectSlug: string): string => {
  return PROJECT_STYLES[projectSlug] || '!font-[bio-sans] !text-core-gray-700'
}

export const createHowPreOrdersWorkAnswer = (projectSlug: string, projectName: string) => {
  const answerComponent = (t: TranslateFunction) => {
    const styleClass = getProjectStyle(projectSlug)
    return (
      <Translate t={t} i18nKey="howPreOrdersWorkAnswer">
        <ul className={`flex list-inside list-disc flex-col gap-2 ${styleClass}`}>
          <li>
            Your ticket codes will be delivered via email (check your spam/junk folder if you don&apos;t receive them
            within 10-15 minutes after ordering).
          </li>
          <li>
            Ticket codes are valid for the specific ticket type purchased and can be redeemed during the theatrical run
            at participating theaters listed on{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </li>
          <li>
            Once showtimes become available, ticket codes can ONLY be redeemed at{' '}
            <a className="underline" target="_blank" href="https://angel.com/tickets">
              angel.com/tickets
            </a>
            .
          </li>
          <li>
            Pre-orders are 100% refundable up to 120 days after purchase of your ticket codes. For additional terms,
            please visit{' '}
            <a className="underline" target="_blank" href="https://angel.com/terms">
              angel.com/terms
            </a>
            .
          </li>
        </ul>
      </Translate>
    )
  }
  answerComponent.displayName = `HowPreOrdersWorkAnswer_${projectName}`
  return answerComponent
}

export const createContactSupportAnswer = (projectSlug: string) => {
  const answerComponent = (t: TranslateFunction) => {
    const styleClass = getProjectStyle(projectSlug)
    return (
      <Translate t={t} i18nKey="howToContactSupportAnswer2">
        <span className={styleClass}>
          If you need assistance with your pre-order, simply click{' '}
          <a className="underline" target="_blank" href="https://angel.com/help">
            here
          </a>{' '}
          to contact our support team at Angel.
        </span>
      </Translate>
    )
  }
  answerComponent.displayName = `ContactSupportAnswer_${projectSlug}`
  return answerComponent
}

export const createRefundPolicyAnswer = (projectSlug: string, hasGiveaway: boolean = false) => {
  const answerComponent = (t: TranslateFunction) => {
    const styleClass = getProjectStyle(projectSlug)

    if (hasGiveaway) {
      return (
        <Translate t={t} i18nKey="refundPolicyGiveawayAnswer">
          <span className={styleClass}>
            You can request a full refund within 120 days of purchase or receive store credit after that, up until six
            weeks after the movie release date. For packages that include perks, refunds are available until any perk is
            used. Similarly, for tickets or packages that include sweepstakes entries, refunds are possible until
            winners are selected or other specified conditions are met. Please refer to the official rules for exact
            dates and details. Refunds are available when no showtimes are within 50 miles of your home. To request a
            refund, please{' '}
            <a
              className="underline"
              target="_blank"
              href="https://support.angel.com/hc/en-us/requests/new?tf_20897151291671=yes_ticket_purcahsed_field&ticket_form_id=20949720698391"
            >
              click here
            </a>
            .
          </span>
        </Translate>
      )
    }

    return (
      <Translate t={t} i18nKey="refundPolicyAnswer">
        <span className={styleClass}>
          Full refunds are available within 120 days of purchase. After 120 days, you may receive store credit up until
          six weeks after the movie release date. For packages with perks or sweepstakes entries: if a perk has been
          used, refunds are no longer available. If a package includes sweepstakes entries, refunds are only possible
          until winners are selected or other specified conditions are met. See the Official Rules for exact dates and
          conditions.
        </span>
      </Translate>
    )
  }
  answerComponent.displayName = `RefundPolicyAnswer_${projectSlug}_${hasGiveaway ? 'Giveaway' : 'Standard'}`
  return answerComponent
}
